import React, { useMemo } from 'react';
import { TourStepContent } from '@shared/components/Tour';
import { useTranslation } from '@shared/core';
import { AccommodationCard } from '../../Accommodations.types';
import { useAccommodationsContext } from '../../context';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { HotelTourStepMap } from './HotelTourStepMap';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { addError } from '@shared/utils/logger';
import { CheckOutline } from '@withjoy/joykit/icons';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsAccommodationsTourEnabled } from '@shared/utils/accommodations/useIsAccommodationsTourEnabled';
import { HotelPrice } from '../HotelPrice';

type HighlightTag = 'AMENITIES' | 'DISTANCE' | 'PRICE_DISCOUNT' | 'USED_BY_EVENTS';

type HotelTourStepContentProps = Readonly<{
  hotel: AccommodationCard;
  aiRecommendationsNoteEnabled: boolean;
}>;

/**
 * Tour step content component that displays information about a specific hotel accommodation.
 * Shows hotel details including distance from venue, rating, and an interactive map.
 * Displays either AI-generated recommendation text or highlight tags based on feature flag.
 *
 * @param hotel - The accommodation card data to display
 * @param aiRecommendationsNoteEnabled - Feature flag to determine which content to show
 */
export const HotelTourStepContent = React.memo<HotelTourStepContentProps>(({ hotel, aiRecommendationsNoteEnabled }) => {
  const { tq } = useTranslation('travelMap');
  const { venueData } = useAccommodationsContext();

  const isMobile = useIsMobileScreen();

  const { isAccommodationsTourEnabled } = useIsAccommodationsTourEnabled();
  const [accommodationsTourStepCompactViewEnabled] = useIsFeatureEnabled('accommodationsTourStepCompactView', { skip: !isAccommodationsTourEnabled });

  const { price, strikeOutPrice } = useTravelPlacesPricingAndBookingDetailsFragment(hotel.id, 'accommodationPlace', !hotel.id);

  const origin = hotel.lat && hotel.lng ? { lat: hotel.lat, lng: hotel.lng } : undefined;
  const destination = venueData?.latitude && venueData?.longitude ? { lat: venueData.latitude, lng: venueData.longitude } : undefined;

  const selectedTag = useMemo((): { title: string; description: string } | null => {
    const highlightTags = hotel.autoSuggestionRecommendation?.highlightTags;
    if (!highlightTags || highlightTags.length === 0) {
      return null;
    }

    const tagDisplayMap: Readonly<Record<HighlightTag, { title: string; description: string }>> = {
      PRICE_DISCOUNT: { title: tq`Great deal for your guests`, description: tq`Unlock special event pricing you can't find anywhere else.` },
      DISTANCE: { title: tq`Close to your venue`, description: tq`Just minutes away to save your guests' time and effort.` },
      USED_BY_EVENTS: { title: tq`Most Trusted`, description: tq`Stays loved by other Joy couples and their guests.` },
      AMENITIES: { title: tq`Top-tier amenities`, description: tq`Everything guests might need for a comfortable stay.` },
    } as const;

    // Always use the first tag
    let firstTag = highlightTags[0];

    if (firstTag === 'USER_RATING') {
      firstTag = highlightTags[1];
    }

    // Type guard to check if firstTag is a valid HighlightTag
    const isValidHighlightTag = (tag: string): tag is HighlightTag => {
      return tag in tagDisplayMap;
    };

    if (!isValidHighlightTag(firstTag) || !firstTag || firstTag === ('USER_RATING' as HighlightTag)) {
      addError(`Unknown highlight tag: ${firstTag} for hotel ${hotel.displayName}`, { context: 'AccommodationsTourStepContent' });
      return null;
    }

    return tagDisplayMap[firstTag];
  }, [hotel, tq]);

  const isCollapsed = isMobile && accommodationsTourStepCompactViewEnabled;

  const contentProps = useMemo(() => {
    if (aiRecommendationsNoteEnabled || !selectedTag) {
      const recommendationText = hotel.autoSuggestionRecommendation?.whyThisHotelIsAGreatPick;
      if (recommendationText) {
        return {
          title: isCollapsed ? tq`Why it’s our pick?` : tq`Why is ${hotel.displayName} our pick?`,
          benefits: [recommendationText],
          isCollapsed,
          renderPrice: (
            <HotelPrice
              accommodation={hotel}
              showComparativePrice={false}
            />
          ),
        };
      }
    }

    return { isCollapsed };
  }, [hotel, aiRecommendationsNoteEnabled, tq, selectedTag, isCollapsed]);

  return (
    <TourStepContent {...contentProps}>
      <>
        {origin && destination && !isMobile && (
          <Box
            width="100%"
            height={pxToRem(180)}
            flex={1}
            marginBottom={5}
            borderRadius={pxToRem(12)}
          >
            <JoyInteractableMapProvider>
              <HotelTourStepMap
                key={`accommodations-tour-step-map-${hotel.id}`}
                originList={[origin]}
                property={{ price, strikeOutPrice }}
                destination={destination}
              />
            </JoyInteractableMapProvider>
          </Box>
        )}
        {!aiRecommendationsNoteEnabled && selectedTag && (
          <>
            {!isCollapsed && (
              <TextV2
                typographyVariant="hed3"
                marginBottom={5}
              >
                {tq`Why is ${hotel.displayName} our pick?`}
              </TextV2>
            )}
            <Flex gap={5}>
              <Box flexShrink={0}>
                <CheckOutline size={32} />
              </Box>
              <Flex
                flexDirection="column"
                gap={1}
              >
                <TextV2 typographyVariant="button2">{selectedTag.title}</TextV2>
                <TextV2 typographyVariant="body1">{selectedTag.description}</TextV2>
              </Flex>
            </Flex>
          </>
        )}
      </>
    </TourStepContent>
  );
});

HotelTourStepContent.displayName = 'HotelTourStepContent';
